import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { screensApi } from '../screens/Screens.api';
import { Screen } from '../screens/Screens.types';
import { parseScreenLocation, ScreenLocationMap } from '../screens/ScreenLocationDialog';
import { OnDemandQueue } from './OnDemandQueue';
import { ON_DEMAND_PATH } from './OnDemand.types';
import { OnDemandWizard } from './OnDemandWizard';

/** Beam on Screen → one screen's On Demand Player (`/#/beamonscreen/ods/:screenId`). */
export const OnDemandScreenPage: React.FC = () => {
    const { screenId = '' } = useParams();
    const [screen, setScreen] = useState<Screen | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshToken, setRefreshToken] = useState(0);
    const [queueCount, setQueueCount] = useState(0);
    const onCount = useCallback((count: number) => setQueueCount(count), []);

    useEffect(() => {
        let cancelled = false;
        const load = (first: boolean) => {
            if (first) setLoading(true);
            screensApi
                .get(screenId)
                .then((next) => {
                    if (!cancelled) {
                        setScreen(next);
                        setError(null);
                    }
                })
                .catch((err) => {
                    if (!cancelled) setError(err instanceof Error ? err.message : 'Screen not found');
                })
                .finally(() => {
                    if (!cancelled && first) setLoading(false);
                });
        };
        load(true);
        const timer = window.setInterval(() => load(false), 10_000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [screenId]);

    const position = parseScreenLocation(screen?.location || '');

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} pr={{ xs: 1.25, md: 2.5 }} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: ON_DEMAND_PATH, itemLabel: 'On Demand Player' },
                    { itemPath: `${ON_DEMAND_PATH}/${screenId}`, itemLabel: screen?.name || 'Screen' },
                ]}
            />
            {loading && (
                <Box display="flex" justifyContent="center" py={6}><CircularProgress color="secondary" /></Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && !error && screen && (
                <>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, width: '100%' }}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{screen.name}</Typography>
                            <Chip size="small" color={screen.online ? 'success' : 'default'} label={screen.online ? 'Online' : 'Offline'} />
                            <Chip size="small" variant="outlined" label={`${queueCount} in queue`} />
                        </Box>
                        {position ? (
                            <Box sx={{ height: 280 }}>
                                <ScreenLocationMap position={position} fill />
                            </Box>
                        ) : (
                            <Alert severity="info">This screen has no location yet.</Alert>
                        )}
                    </Paper>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                        <OnDemandWizard screenId={screenId} onScheduled={() => setRefreshToken((value) => value + 1)} />
                        <OnDemandQueue screenId={screenId} refreshToken={refreshToken} onCount={onCount} />
                    </Box>
                </>
            )}
        </Box>
    );
};
